import { useState, useEffect, useLayoutEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { MdLocationOn, MdSearch, MdClose, MdMyLocation } from 'react-icons/md';
import { useLocationSearch } from '../hooks/useLocationSearch';

// Helper to check if mobile
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640;

const PORTAL_ROOT_ID = 'location-dropdown-portal-root';

// Jedan stabilan DOM container za portal — sprečava dupli dropdown (npr. kod dva instance ili flash na 0,0)
function getOrCreatePortalRoot() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(PORTAL_ROOT_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = PORTAL_ROOT_ID;
    document.body.appendChild(el);
  }
  return el;
}

const LocationAutocomplete = forwardRef(function LocationAutocomplete({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Unesite grad ili naselje…",
  className = "",
  variant = "default",
  debounceMs = 300,
  onKeyDown
}, ref) {
  const { suggestions, isLoading, search, clear, searchNearby } = useLocationSearch();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const justSelectedRef = useRef(false);
  const valueSetByNearbyRef = useRef(false);
  const suppressOpenUntilRef = useRef(0);
  
  // Search on value change (ne traži kad je vrednost postavljena iz "Pretraži u blizini")
  useEffect(() => {
    if (valueSetByNearbyRef.current) {
      valueSetByNearbyRef.current = false;
      setShowSuggestions(false);
      return;
    }
    if (value && value.length >= 3) {
      search(value, debounceMs);
    } else {
      clear();
      setShowSuggestions(false);
    }
  }, [value, search, clear, debounceMs]);
  
  // Update dropdown position from container rect (for Portal)
  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Show suggestions when they arrive (ne otvaraj odmah posle izbora iz liste)
  useEffect(() => {
    if (justSelectedRef.current) return;
    if (Date.now() < suppressOpenUntilRef.current) return;
    if (suggestions.length > 0 && value && value.length >= 3) {
      setShowSuggestions(true);
    }
  }, [suggestions, value]);

  // Pozicija pre paint-a da dropdown nikad ne „skoci” — odmah merimo kad se otvori
  useLayoutEffect(() => {
    if (showSuggestions && (suggestions.length > 0 || isLoading)) {
      updatePosition();
    }
  }, [showSuggestions, suggestions.length, isLoading]);

  // Držimo poziciju u toku pri skrolu/resize kad je dropdown otvoren
  useEffect(() => {
    if (!showSuggestions || (suggestions.length === 0 && !isLoading)) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions, suggestions.length, isLoading]);
  
  const handleInputChange = (e) => {
    const query = e.target.value;
    onChange(query);
    
    if (query.length >= 3) {
      // Will show when suggestions arrive via useEffect
    } else {
      setShowSuggestions(false);
    }
  };
  
  const handleSelect = (suggestion) => {
    justSelectedRef.current = true;
    suppressOpenUntilRef.current = Date.now() + 1500;
    setShowSuggestions(false);
    clear();
    onChange(suggestion.label);
    inputRef.current?.blur();
    setTimeout(() => { justSelectedRef.current = false; }, 500);
    if (onSelect) {
      onSelect({
        label: suggestion.label,
        city: suggestion.city,
        lat: suggestion.lat,
        lon: suggestion.lon
      });
    }
  };
  
  // Close suggestions when clicking outside (container or portaled dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setShowSuggestions(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setShowSuggestions(false);
    clear();
    inputRef.current?.focus();
  };

  const handleSearchNearby = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowSuggestions(false);
    clear(); // očisti stare suggestions da se dropdown ne prikaže ponovo
    justSelectedRef.current = true;
    suppressOpenUntilRef.current = Date.now() + 2500;
    valueSetByNearbyRef.current = true;
    setTimeout(() => { justSelectedRef.current = false; }, 500);
    
    await searchNearby((location) => {
      onChange(location.label);
      inputRef.current?.blur();
      if (onSelect) {
        onSelect({
          label: location.label,
          city: location.city,
          lat: location.lat,
          lon: location.lon
        });
      }
    });
  };

  // Expose closeDropdown method via ref
  useImperativeHandle(ref, () => ({
    closeDropdown: () => {
      setShowSuggestions(false);
    }
  }));

  const isLanding = variant === 'landing';

  return (
    <div ref={containerRef} className={`relative h-full flex items-center ${className}`}>
      <div className="relative w-full h-full flex items-center">
        <MdSearch
          className={`absolute top-1/2 -translate-y-1/2 pointer-events-none z-10 left-4 ${
            isLanding
              ? 'text-[var(--color-text-muted)] text-lg opacity-70'
              : `text-gray-400 text-xl sm:text-2xl left-5 sm:left-5 ${
                  isMobile() ? ((isFocused || !!value) ? 'opacity-100' : 'opacity-0') : 'opacity-100'
                } transition-opacity`
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setIsFocused(true);
            if (justSelectedRef.current) return;
            if (Date.now() < suppressOpenUntilRef.current) return;
            if (suggestions.length > 0 || value.length >= 3) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            setTimeout(() => setIsFocused(false), 120);
          }}
          placeholder={placeholder}
          className={
            isLanding
              ? 'block w-full min-h-[48px] py-2.5 m-0 text-[15px] sm:text-base bg-transparent border-0 focus:outline-none focus:ring-0 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] rounded-xl'
              : 'block w-full min-h-[52px] sm:min-h-[44px] py-2.5 sm:py-2 m-0 text-base sm:text-[16px] bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-full sm:rounded-none'
          }
          style={{
            lineHeight: 1.2,
            textAlign: isLanding ? 'left' : (isMobile() ? ((isFocused || !!value) ? 'left' : 'center') : 'left'),
            paddingLeft: isLanding ? '2.75rem' : '3.75rem',
            paddingRight: value
              ? (isMobile() ? '7.25rem' : '7rem')
              : (isMobile() ? '4.25rem' : '3.5rem'),
          }}
        />
        <button
          type="button"
          onClick={handleSearchNearby}
          className={`absolute top-1/2 -translate-y-1/2 transition-colors p-1.5 ${
            isLanding
              ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] right-3'
              : `text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 ${
                  isMobile() && !value ? 'right-7' : (value ? 'right-14' : 'right-6')
                }`
          }`}
          aria-label="Pretraži u blizini"
          title="Pretraži u blizini (2km)"
        >
          <MdMyLocation className="text-xl" />
        </button>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 ${isMobile() ? 'right-4' : 'right-5'}`}
            aria-label="Obriši"
          >
            <MdClose className="text-lg" />
          </button>
        )}
        {isLoading && !value && (
          <div className={`absolute top-1/2 -translate-y-1/2 ${isMobile() ? 'right-10' : 'right-12'}`}>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}
      </div>
      
      {/* Dropdown u jedan stabilan portal root — sprečava dupli popup i ghost na (0,0) */}
      {showSuggestions && (suggestions.length > 0 || isLoading) && (() => {
        const rect = containerRef.current?.getBoundingClientRect();
        const pos = rect
          ? { top: rect.bottom + 8, left: rect.left, width: Math.max(rect.width, 280) }
          : dropdownPosition;
        // Ne renderuj dropdown na (0,0) — čeka se validna pozicija (izmerena ili iz state)
        const hasValidPosition = rect
          ? rect.width > 0
          : (dropdownPosition.width > 0 || dropdownPosition.left > 0 || dropdownPosition.top > 8);
        if (!hasValidPosition) return null;

        const portalRoot = getOrCreatePortalRoot();
        if (!portalRoot) return null;

        const Wrapper = (
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2147483647,
              pointerEvents: 'none',
            }}
          >
            <div
              ref={dropdownRef}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-y-auto"
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: Math.min(pos.width || 320, typeof window !== 'undefined' ? window.innerWidth - 24 : 400),
                maxHeight: typeof window !== 'undefined' ? Math.min(400, window.innerHeight - pos.top - 16) : 400,
                pointerEvents: 'auto',
              }}
            >
              {suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-start gap-3"
                  >
                    <MdLocationOn className="text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {suggestion.city || suggestion.label.split(',')[0]}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {suggestion.label}
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
        return createPortal(Wrapper, portalRoot);
      })()}
    </div>
  );
});

export default LocationAutocomplete;
